import React, { createContext, useState, useEffect, useCallback } from 'react';
import { Popper, Paper, Typography, Button, Box, Backdrop } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TutorialKey, TutorialContextType } from '../types/Tutorial';
import { getTutorialSteps, TutorialStep } from '../utils/tutorialSteps';
import { apiFetch } from '../apiFetch';

export const TutorialContext = createContext<TutorialContextType | null>(null);

const TourOverlay: React.FC<{
  steps: TutorialStep[];
  onFinish: () => void;
  onSkip: () => void;
}> = ({ steps, onFinish, onSkip }) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;

  // Find and set anchor element for current step, elevate it above backdrop
  useEffect(() => {
    if (!step) return;

    let el: HTMLElement | null = null;

    const findElement = () => {
      el = document.querySelector(step.target) as HTMLElement | null;
      if (el) {
        setAnchorEl(el);
        // Elevate element above the backdrop without disrupting internal stacking
        el.style.zIndex = '1300';
        el.style.outline = '3px solid rgba(25, 118, 210, 0.7)';
        el.style.outlineOffset = '4px';
        el.style.borderRadius = '4px';
        // Give it a solid background so the dark backdrop doesn't show through
        const computed = window.getComputedStyle(el);
        if (!computed.backgroundColor || computed.backgroundColor === 'rgba(0, 0, 0, 0)' || computed.backgroundColor === 'transparent') {
          el.setAttribute('data-tutorial-bg-added', 'true');
          el.style.backgroundColor = 'var(--mui-palette-background-default, #fff)';
        }
        // Only set position if element isn't already positioned
        if (computed.position === 'static') {
          el.style.position = 'relative';
        }
        // Scroll element into view
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setAnchorEl(null);
      }
    };

    // Small delay to let DOM settle
    const timer = setTimeout(findElement, 100);
    return () => {
      clearTimeout(timer);
      // Clean up styles when step changes
      if (el) {
        const computed = window.getComputedStyle(el);
        // Only clear position if we set it
        if (computed.position === 'relative') {
          el.style.position = '';
        }
        el.style.zIndex = '';
        el.style.outline = '';
        el.style.outlineOffset = '';
        el.style.borderRadius = '';
        if (el.getAttribute('data-tutorial-bg-added') === 'true') {
          el.style.backgroundColor = '';
          el.removeAttribute('data-tutorial-bg-added');
        }
      }
    };
  }, [step]);

  // Skip steps whose target elements don't exist
  useEffect(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el && !isLast) {
      // Target not found, skip to next step
      setCurrentStep((prev) => prev + 1);
    } else if (!el && isLast) {
      // Last step target not found, finish
      onFinish();
    }
  }, [step, isLast, onFinish]);

  const handleNext = () => {
    if (isLast) {
      onFinish();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirst) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  if (!step || !anchorEl) return null;

  return (
    <>
      <Backdrop
        open
        sx={{
          zIndex: 1299,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
        onClick={onSkip}
      />
      <Popper
        open
        anchorEl={anchorEl}
        placement="bottom"
        sx={{ zIndex: 1301 }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 12] } },
          { name: 'preventOverflow', options: { padding: 16 } },
          { name: 'flip', options: { fallbackPlacements: ['top', 'right', 'left'] } },
        ]}
      >
        <Paper
          elevation={8}
          sx={{
            p: 2.5,
            maxWidth: 360,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {step.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {step.content}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mb: 1.5, display: 'block' }}>
            {currentStep + 1} of {steps.length}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onSkip}
              sx={{ color: 'text.secondary', borderColor: 'divider', textTransform: 'none' }}
            >
              Don't show again
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isFirst && (
                <Button size="small" onClick={handleBack}>
                  Back
                </Button>
              )}
              <Button size="small" variant="contained" onClick={handleNext}>
                {isLast ? 'Done' : 'Next'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Popper>
    </>
  );
};

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [completedTutorials, setCompletedTutorials] = useState<Record<string, boolean>>({});
  const [activeTour, setActiveTour] = useState<TutorialKey | null>(null);
  const [apiSteps, setApiSteps] = useState<Record<string, TutorialStep[]> | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch tooltip overrides from public API on mount
  useEffect(() => {
    const fetchTooltips = async () => {
      try {
        const resp = await fetch(`${apiUrl}/api/tutorials/tooltips/`);
        if (resp.ok) {
          const data = await resp.json();
          setApiSteps(data.sections || {});
        }
      } catch {
        // Silently fail — will use hardcoded defaults
      }
    };
    fetchTooltips();
  }, [apiUrl]);

  // Fetch completed tutorials on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    const fetchProfile = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/account/profile/`);
        setCompletedTutorials(data.completed_tutorials || {});
      } catch (err) {
        // Silently fail
      }
    };
    fetchProfile();
  }, [apiUrl]);

  // Listen for auth changes to reset state
  useEffect(() => {
    const handleAuthChange = () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setCompletedTutorials({});
        setActiveTour(null);
      }
    };
    window.addEventListener('authChange', handleAuthChange);
    return () => window.removeEventListener('authChange', handleAuthChange);
  }, []);

  const markCompleted = useCallback(async (key: TutorialKey) => {
    setCompletedTutorials((prev) => ({ ...prev, [key]: true }));

    try {
      await apiFetch(`${apiUrl}/api/account/profile/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed_tutorials: { [key]: true } }),
      });
    } catch (err) {
      // Silently fail - local state already updated
    }
  }, [apiUrl]);

  const resetAll = useCallback(async () => {
    setCompletedTutorials({});

    try {
      await apiFetch(`${apiUrl}/api/account/profile/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_tutorials: true }),
      });
    } catch (err) {
      // Silently fail
    }
  }, [apiUrl]);

  const shouldShowTour = useCallback((key: TutorialKey) => {
    return !completedTutorials[key];
  }, [completedTutorials]);

  const startTour = useCallback((key: TutorialKey) => {
    setActiveTour(key);
  }, []);

  const handleTourFinish = useCallback(() => {
    if (activeTour) {
      markCompleted(activeTour);
    }
    setActiveTour(null);
  }, [activeTour, markCompleted]);

  const handleTourSkip = useCallback(() => {
    if (activeTour) {
      markCompleted(activeTour);
    }
    setActiveTour(null);
  }, [activeTour, markCompleted]);

  // Use API steps if loaded, otherwise fall back to hardcoded defaults
  const steps = activeTour
    ? (apiSteps && apiSteps[activeTour] ? apiSteps[activeTour] : getTutorialSteps(activeTour))
    : [];

  return (
    <TutorialContext.Provider value={{ completedTutorials, activeTour, markCompleted, resetAll, shouldShowTour, startTour }}>
      {children}
      {activeTour && steps.length > 0 && (
        <TourOverlay
          steps={steps}
          onFinish={handleTourFinish}
          onSkip={handleTourSkip}
        />
      )}
    </TutorialContext.Provider>
  );
};
